// ── Esquemas dentro de la documentación ──────────────────────────────────────
//
// Un manual de ERP escrito solo con párrafos se lee una vez y no se vuelve. Lo
// que hace falta para entender de un vistazo —qué pasa antes de qué, qué cuelga
// de qué, qué puede cada nivel— es un dibujo, y hasta ahora no había forma de
// poner uno: el renderizador escapa el HTML a propósito y no tiene sintaxis de
// imagen.
//
// ── Por qué NO son imágenes ─────────────────────────────────────────────────
//
// Una captura de pantalla envejece antes que el texto que acompaña: se rehace
// un botón y el manual enseña el de antes, sin que falle nada ni se entere
// nadie. Y un PNG no se traduce, no se busca, no se lee con un lector de
// pantalla y pesa.
//
// Un esquema de aquí es TEXTO en el `.md` del módulo: se traduce con el resto,
// se busca, se selecciona, se lee en voz alta, y se ve igual de nítido en un
// móvil que en una pantalla de 27 pulgadas. Lo que se dibuja son cajas y
// líneas; lo que las llena es la misma frase que estaba escrita.
//
// ── Por qué se lee bien aunque no se dibuje ─────────────────────────────────
//
// Van dentro de una valla de código con nombre (```pasos), que es Markdown
// legal. Donde nadie sepa dibujarlos —el repositorio, un editor, un `cat`—
// salen como un bloque de texto perfectamente legible, porque la sintaxis está
// pensada para leerse: una línea por caja, el nombre delante y la explicación
// detrás de una barra.
//
// Tres tipos, que son los que la documentación de este ERP pedía ya escritos en
// prosa:
//
//   pasos    lo que ocurre en orden        (la inspección de una subida)
//   niveles  una escalera, de menos a más  (lector → editor → gestor)
//   arbol    lo que cuelga de lo que       (carpetas, y quién hereda qué)

/** Los nombres de valla que este módulo dibuja. El resto siguen siendo código. */
export const DIAGRAMS = ['pasos', 'niveles', 'arbol']

/**
 * Una línea `Nombre | explicación` partida en sus dos mitades.
 *
 * La barra es opcional: una caja puede ser solo un nombre, y entonces la
 * explicación es vacía en lugar de ser la misma frase repetida.
 */
function split(line) {
  const at = line.indexOf('|')
  if (at < 0) return { label: line.trim(), note: '' }
  return { label: line.slice(0, at).trim(), note: line.slice(at + 1).trim() }
}

/**
 * La sangría de una línea, en niveles de dos espacios.
 *
 * Dos y no cuatro porque un árbol de documentación baja cuatro o cinco ramas y
 * con cuatro espacios el fichero se sale del margen antes de llegar al fondo.
 * Se admite cualquier múltiplo: lo que cuenta es la proporción.
 */
function depthOf(line) {
  const spaces = line.match(/^ */)[0].length
  return Math.floor(spaces / 2)
}

/**
 * Un esquema, en HTML.
 *
 * Recibe el contenido YA ESCAPADO —igual que todo lo que produce el
 * renderizador— y devuelve marcado con clases y nada más: ni estilos en línea,
 * ni atributos que vengan del texto. Lo que se dibuja lo deciden las clases, y
 * las clases las escribe este fichero.
 *
 * @param {string} kind    uno de DIAGRAMS
 * @param {string[]} lines las líneas de dentro de la valla, ya escapadas
 * @returns {string} HTML, o cadena vacía si no hay nada que dibujar
 */
export function renderDiagram(kind, lines) {
  const rows = lines.filter(line => line.trim())
  if (!rows.length) return ''

  if (kind === 'pasos') return steps(rows)
  if (kind === 'niveles') return levels(rows)
  if (kind === 'arbol') return tree(rows)
  return ''
}

/**
 * Lo que ocurre en orden, en cajas con una flecha entre ellas.
 *
 * La flecha es un pseudo-elemento del CSS y no un carácter del texto: así no se
 * selecciona al copiar, no la lee un lector de pantalla —que ya sabe que es una
 * lista ordenada— y no hay que decidir si apunta a la derecha o hacia abajo
 * cuando la pantalla es estrecha. Eso lo decide la hoja de estilos.
 */
function steps(rows) {
  const items = rows.map(row => {
    const { label, note } = split(row)
    return `<li class="dg-step">`
      + `<span class="dg-step__label">${label}</span>`
      + (note ? `<span class="dg-step__note">${note}</span>` : '')
      + `</li>`
  })
  return `<ol class="dg dg--steps">${items.join('')}</ol>`
}

/**
 * Una escalera: cada peldaño incluye lo que puede el de abajo.
 *
 * Numerada, y el número se dibuja como el ancho de la barra: lo que se entiende
 * de un vistazo es que cada nivel es MÁS, no cuántos hay.
 */
function levels(rows) {
  const items = rows.map((row, index) => {
    const { label, note } = split(row)
    // El ancho va en un `style`, que es el único sitio donde este fichero
    // escribe un atributo — y el valor es un número calculado aquí, nunca algo
    // que venga del texto.
    const width = Math.round(((index + 1) / rows.length) * 100)
    return `<li class="dg-level">`
      + `<span class="dg-level__bar" style="width:${width}%"></span>`
      + `<span class="dg-level__label">${label}</span>`
      + (note ? `<span class="dg-level__note">${note}</span>` : '')
      + `</li>`
  })
  return `<ol class="dg dg--levels">${items.join('')}</ol>`
}

/**
 * Lo que cuelga de lo que, por sangría.
 *
 * La línea vertical que une una rama con su padre la dibuja el CSS, así que el
 * texto no lleva ni un `│` ni un `└`: un árbol hecho con esos caracteres se
 * descuadra en cuanto alguien traduce una palabra más larga, y eso es
 * exactamente lo que pasa en un manual en cuatro idiomas.
 */
function tree(rows) {
  const items = rows.map(row => {
    const { label, note } = split(row)
    // Topada a cuatro: más hondo que eso no se entiende en un dibujo, y es la
    // señal de que ese árbol quería ser dos.
    const depth = Math.min(depthOf(row), 4)
    return `<li class="dg-node dg-node--${depth}">`
      + `<span class="dg-node__label">${label}</span>`
      + (note ? `<span class="dg-node__note">${note}</span>` : '')
      + `</li>`
  })
  return `<ul class="dg dg--tree">${items.join('')}</ul>`
}
